import { useQuery } from '@tanstack/react-query';
import { ProgressSpinner } from 'primereact/progressspinner';
import ErrorMessage from '../../../../components/messages/ErrorMessage';
import { useCompanyContext } from '../../../../context/CompanyContext';
import {
  EmployeeTimesheet,
  PayrollTimesheetDetails,
  PRTimesheetSummary,
  TimeSheetArgs,
} from '../../../../Interfaces/Accounting/ACForms.interfaces';
import {
  fetchPREmployeesTimesheet,
  fetchPRTimesheets,
  fetchPRUserPreferences,
} from '../../../../services/accounting/PayrollService';
import TimesheetTable from './TimesheetTable';
import React, { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { PRTimesheetFilters } from './TimesheetFilters';
import { v4 as uuidv4 } from 'uuid';
import { useRolesAccessContext } from '../../../../context/RolesAccessContext';
import TimesheetProcess from './TimesheetProcess';
import PCReimbursementTable from '../PettyCashReimbursement/PCReimbursementTable';
import { EmployeePCReimbursement } from '../../../../Interfaces/Accounting/Payroll.interface';
import { FILE_20_MB_LIMIT } from '@/src/utils/fileConsts';
import {
  formatReimbursementEmployees,
  updateAttachments,
} from '../PettyCashReimbursement/utils';
import TimesheetBundleProcess from './TimesheetBundleProcess';
import { formatTimesheetEmployees } from './util';
import TimesheetBundleSteps from './TimesheetBundleSteps';
import ErrorToast, {
  showToast,
} from '../../../../components/messages/ErrorAlert';
import { Toast } from 'primereact/toast';
import { useProcessContext } from '../../../../context/ProcessContext';
import { fecthJobs } from '../../../../services/accounting/JobsContractsService';
import ApprovalHistory from '../../../../components/approvals/ApprovalHistory';

type TimeSheetTableContainerProps = {
  periodStart: Date;
  periodEnd: Date;
  defaultTimesheet?: TimeSheetArgs;
  defaultLoading?: boolean;
};

const TimeSheetTableContainer = ({
  periodStart,
  periodEnd,
  defaultTimesheet,
  defaultLoading,
}: TimeSheetTableContainerProps) => {
  const { selectedCompany } = useCompanyContext();
  const { rolesAcess } = useRolesAccessContext();
  const processStatus = useProcessContext();
  const { watch } = useFormContext<PRTimesheetFilters>();
  const submittedID = watch('submittedID');
  const [draftID, setDraftID] = useState('');
  const [employees, setEmployees] = useState<EmployeeTimesheet[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [reimbursement, setReimbursement] = useState<EmployeePCReimbursement[]>(
    []
  );
  const [fileLimit, setFileLimit] = useState<number>(FILE_20_MB_LIMIT);
  const [reimbursementID, setReimbursementID] = useState<string>();
  const [useBundleProcess, setUseBundleProcess] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [filesToDeleteDefault, setFilesToDeleteDefault] = useState<
    Record<number, string[]>
  >([]);
  const [step, setStep] = useState(0);
  const [shouldRemoveReimbursement, setShouldRemoveReimbursement] =
    useState(false);
  const [fromLastTimesheet, setFromLastTimesheet] = useState(false);
  const access = rolesAcess?.find(
    (access) => access.report === 'new_pr_timesheet'
  );
  const currentdefTimesheet = useRef(defaultTimesheet);
  const toast = useRef<Toast>(null);

  const jobs = useQuery({
    queryKey: ['getPRJobs', selectedCompany],
    queryFn: ({ signal }) =>
      fecthJobs({ companyID: selectedCompany!.id, status: [1] }, signal),
    refetchOnWindowFocus: false,
    enabled: !!selectedCompany,
  });

  const { data, isError } = useQuery({
    queryKey: ['getPREmployeesTimesheet', selectedCompany, submittedID],
    queryFn: ({ signal }) => {
      setIsLoading(true);
      return fetchPREmployeesTimesheet(
        selectedCompany!.id,
        access?.shouldApprove ? submittedID : undefined,
        signal
      );
    },
    refetchOnWindowFocus: false,
    enabled: !!selectedCompany && !jobs.isLoading && !jobs.error,
    onSuccess: (data) => {
      setDraftID(data.draft?.id ?? '');
      const reimbursement = data.draft?.reimbursement;
      const tempVersion = localStorage.getItem('temp-timesheet');
      const tempVersionID = localStorage.getItem('temp-timesheet-id');
      const tempVersionTime = localStorage.getItem('temp-timesheet-updatedAt');
      const tempCompany = localStorage.getItem('temp-company');
      const isApproval = localStorage.getItem('temp-approval') === 'true';
      const draftLastUpdate = data.draft?.updatedAt;
      let backupUsed = false;
      const isSameDraft =
        (data.draft?.id === tempVersionID ||
          (!data.draft?.id && !tempVersionID)) &&
        selectedCompany?.id === tempCompany &&
        isApproval === !!submittedID;

      if (
        isSameDraft &&
        tempVersionTime &&
        (!draftLastUpdate ||
          new Date(tempVersionTime).getTime() >
            new Date(draftLastUpdate).getTime()) &&
        tempVersion
      ) {
        setEmployees(JSON.parse(tempVersion));
        backupUsed = true;
      } else {
        setEmployees(formatTimesheetEmployees(data.employees, jobs.data));
      }

      const tempRemVersion = localStorage.getItem('temp-reimbursement');
      const tempRemVersionTime = localStorage.getItem(
        'temp-reimbursement-updatedAt'
      );
      const drafRemtLastUpdate = reimbursement?.updatedAt;

      if (
        isSameDraft &&
        tempRemVersionTime &&
        (!drafRemtLastUpdate ||
          new Date(tempRemVersionTime).getTime() >
            new Date(drafRemtLastUpdate).getTime()) &&
        tempRemVersion
      ) {
        setReimbursementID(reimbursement?.id ?? undefined);
        setUseBundleProcess(true);
        setReimbursement(JSON.parse(tempRemVersion));
        setFileLimit(FILE_20_MB_LIMIT - (reimbursement?.attachmentsSize ?? 0));

        backupUsed = true;
      } else if (reimbursement) {
        setReimbursementID(reimbursement.id);
        setUseBundleProcess(true);
        setReimbursement(formatReimbursementEmployees(reimbursement.employees));
        setFileLimit(FILE_20_MB_LIMIT - reimbursement.attachmentsSize);
      } else {
        setReimbursementID(undefined);
        setUseBundleProcess(false);
        setReimbursement([
          { empID: null, total: null, details: [{ id: uuidv4() }] },
        ]);
        setFileLimit(FILE_20_MB_LIMIT);
      }

      if (backupUsed) {
        localStorage.removeItem('temp-timesheet');
        localStorage.removeItem('temp-timesheet-updatedAt');
        localStorage.removeItem('temp-timesheet-id');
        localStorage.removeItem('temp-approval');
        localStorage.removeItem('temp-company');
        localStorage.removeItem('temp-reimbursement');
        localStorage.removeItem('temp-reimbursement-updatedAt');

        setTimeout(() => {
          showToast(
            'info',
            toast,
            'Payroll Timesheet',
            `Data was restored from temporary backup.${
              tempRemVersion ? ` Files couldn't be restored.` : ''
            }`,
            7000
          );
        }, 1000);

        processStatus.setSave(true);
        processStatus.setSubmit(false);
        processStatus.setApprove(false);
      } else {
        processStatus.setSave(false);
        processStatus.setSubmit(!(data?.draft?.submitted ?? true));
        processStatus.setApprove(submittedID ? true : !!data?.draft?.id);
      }

      setIsLoading(false);
    },
    onError: () => {
      setIsLoading(false);
    },
  });

  const userPreferences = useQuery({
    queryKey: ['getPRUserPreferences'],
    queryFn: () => {
      return fetchPRUserPreferences();
    },
    refetchOnWindowFocus: false,
  });

  const updateEmployeesAttachments = (
    employees: EmployeePCReimbursement[],
    newSizeLimit: number,
    id: string
  ) => {
    const newEmps = updateAttachments(employees, reimbursement);

    setFilesToDeleteDefault({});
    setReimbursement(newEmps);
    setFileLimit(FILE_20_MB_LIMIT - (newSizeLimit ?? 0));
    setReimbursementID(id);
  };

  const resetTimesheet = () => {
    setEmployees((employees) => {
      const cleanedData = employees.map((emp) => ({
        empID: emp.empID,
        lastName: emp.lastName,
        firstName: emp.firstName,
        salaried: emp.salaried,
        details: [{ id: uuidv4(), dateDetails: [{ id: uuidv4() }] }],
      }));

      return cleanedData;
    });
  };

  const resetReimbursement = () => {
    setReimbursement((reimbursement) => {
      const allPrevAttachments: Record<number, string[]> = {};
      reimbursement.forEach((emp) => {
        const prevAttachments = emp.attachments;

        if (prevAttachments) {
          const attachmentNames: string[] = [];
          prevAttachments.forEach((prevAttachment) => {
            attachmentNames.push(prevAttachment.uniqueName);
          });
          allPrevAttachments[emp.empID!] = attachmentNames;
        }
      });

      setFilesToDeleteDefault((def) =>
        !Object.keys(def).length ? allPrevAttachments : def
      );

      return [{ empID: null, total: null, details: [{ id: uuidv4() }] }];
    });
    setFileLimit(FILE_20_MB_LIMIT);
  };

  const resetBundle = () => {
    resetTimesheet();
    resetReimbursement();
  };

  const removeReimbursement = () => {
    resetReimbursement();
    setStep(0);
    setUseBundleProcess(false);
    setShouldRemoveReimbursement(true);
    processStatus.setSave(true);
    localStorage.removeItem('temp-reimbursement');
    localStorage.removeItem('temp-reimbursement-updatedAt');
  };

  useEffect(() => {
    if (defaultTimesheet && defaultTimesheet !== currentdefTimesheet.current) {
      const newEmpData = employees.map((emp: EmployeeTimesheet) => {
        const defaultEmp = defaultTimesheet.find(
          (defaultEmp) => emp.empID === defaultEmp.empID
        );

        let newDetails: PayrollTimesheetDetails[] | null = null;
        if (defaultEmp?.details) {
          newDetails = defaultEmp.details.map((detail) => {
            return {
              ...detail,
              id: (detail as { id?: string }).id ?? uuidv4(),
            };
          });
        }

        return defaultEmp
          ? {
              ...defaultEmp,
              firstName: emp.firstName,
              lastName: emp.lastName,
              salaried: emp.salaried,
              details: newDetails ?? undefined,
            }
          : emp;
      });

      setEmployees(formatTimesheetEmployees(newEmpData, jobs.data));
      setFromLastTimesheet(true);
      currentdefTimesheet.current = defaultTimesheet;
    }
  }, [defaultTimesheet, employees, jobs.data]);

  const submittedTimesheets = useQuery({
    queryKey: ['getSubmittedPRTimesheets', selectedCompany],
    queryFn: ({ signal }) =>
      fetchPRTimesheets(
        {
          companyID: selectedCompany!.id,
          isSubmitted: true,
          isApproved: false,
        },
        signal
      ),
    refetchOnWindowFocus: false,
    enabled: false,
  });

  const afterApproval = (draftID: string) => {
    submittedTimesheets.data?.forEach(
      (draft: PRTimesheetSummary & { disabled?: boolean }) => {
        if (draft.id === draftID) {
          draft.disabled = true;
        }
      }
    );
  };

  if (
    isLoading ||
    defaultLoading ||
    defaultTimesheet !== currentdefTimesheet.current ||
    userPreferences.isFetching
  ) {
    return (
      <div className="text-center mx-auto mt-3">
        <ProgressSpinner />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorMessage
        content={'Failed to obtain data! Please try again later.'}
      />
    );
  }

  return (
    <React.Fragment>
      <ErrorToast toastRef={toast} />;
      {data?.draft?.approvals ? (
        <React.Fragment>
          <div
            className="text-18px mx-3 font-bold cursor-pointer totalColor hover:underline flex w-fit"
            onClick={() => setShowHistory(true)}
          >
            Check Draft Approvals
          </div>
          <ApprovalHistory
            show={showHistory}
            hide={() => {
              setShowHistory(false);
            }}
            approvals={data.draft.approvals}
            lastStep={data.draft.lastStep ?? 0}
          />
        </React.Fragment>
      ) : undefined}
      {useBundleProcess && (
        <div className="mx-3 mb-5 printHide">
          <TimesheetBundleSteps
            index={step}
            changeIndex={(newStep) => {
              setStep(newStep);
            }}
            removeReimbursement={removeReimbursement}
          />
        </div>
      )}
      <span className={`${step !== 0 && 'hidden'}`}>
        <TimesheetTable
          employees={employees}
          showErrors={showErrors}
          periodStart={periodStart}
          periodEnd={periodEnd}
          changeEmployees={(draft) => {
            setEmployees(draft);
            const tempStoredID = localStorage.getItem(
              'temp-timesheet-stored-id'
            );
            localStorage.setItem('temp-timesheet', JSON.stringify(draft));
            localStorage.setItem('temp-approval', `${!!submittedID}`);
            localStorage.setItem(
              'temp-timesheet-id',
              data?.draft?.id ?? tempStoredID ?? ''
            );
            localStorage.setItem(
              'temp-timesheet-updatedAt',
              new Date().toString()
            );
            localStorage.setItem('temp-company', selectedCompany?.id ?? '');
            localStorage.removeItem('temp-timesheet-stored-id');
          }}
          companyHours={data?.companyHours}
          onlyPercentageDefault={userPreferences.data?.['USE_PERCENTAGE']}
        />
      </span>
      <span className={`${step !== 1 && 'hidden'}`}>
        <PCReimbursementTable
          employees={reimbursement}
          draftID={reimbursementID}
          sizeLimit={fileLimit}
          showErrors={showErrors}
          changeEmployees={(draft, ignoreTemp) => {
            if (useBundleProcess && !ignoreTemp) {
              let temp = '';
              if (Array.isArray(draft)) {
                temp = JSON.stringify(
                  draft.map((emp) => {
                    return { ...emp, newAttachments: null };
                  })
                );
                localStorage.setItem('temp-reimbursement', temp);
                setReimbursement(draft);
              } else {
                setReimbursement((list) => {
                  const newData = draft(list);
                  temp = JSON.stringify(
                    newData.map((emp) => {
                      return { ...emp, newAttachments: null };
                    })
                  );
                  localStorage.setItem('temp-reimbursement', temp);
                  return newData;
                });
              }

              localStorage.setItem(
                'temp-reimbursement-updatedAt',
                new Date().toString()
              );
              const tempStoredID = localStorage.getItem(
                'temp-timesheet-stored-id'
              );
              localStorage.setItem(
                'temp-timesheet-id',
                data?.draft?.id ?? tempStoredID ?? ''
              );
              localStorage.setItem('temp-approval', `${!!submittedID}`);
              localStorage.setItem('temp-company', selectedCompany?.id ?? '');
              localStorage.removeItem('temp-timesheet-stored-id');
            } else {
              setReimbursement(draft);
            }
          }}
          changeFileLimit={(size) => {
            setFileLimit(size);
          }}
        />
      </span>
      <div className="mt-3 flex flex-wrap justify-content-center gap-4 mx-3 printHide">
        {useBundleProcess ? (
          <TimesheetBundleProcess
            timesheet={employees ?? []}
            reimbursement={reimbursement}
            periodStart={periodStart}
            periodEnd={periodEnd}
            approvalMode={!!submittedID}
            timesheetID={draftID}
            updateTimesheetID={(id: string) => setDraftID(id)}
            defaultReimbursementID={reimbursementID}
            afterCheck={(show) => {
              setShowErrors(show);
            }}
            fromLastTimesheet={fromLastTimesheet}
            afterUpdate={(data, newSizeLimit, id) => {
              setFromLastTimesheet(false);
              updateEmployeesAttachments(data, newSizeLimit, id);
              localStorage.removeItem('temp-timesheet');
              localStorage.removeItem('temp-timesheet-updatedAt');
              localStorage.removeItem('temp-timesheet-id');
              localStorage.removeItem('temp-approval');
              localStorage.removeItem('temp-reimbursement');
              localStorage.removeItem('temp-reimbursement-updatedAt');
              localStorage.removeItem('temp-company');
            }}
            access={access ?? { report: 'new_pr_timesheet' }}
            step={step}
            changeStep={(newStep) => {
              setStep(newStep);
            }}
            resetDraft={resetBundle}
            filesToDeleteDefault={filesToDeleteDefault}
            afterApproval={afterApproval}
          />
        ) : (
          <TimesheetProcess
            draft={employees ?? []}
            periodStart={periodStart}
            periodEnd={periodEnd}
            approvalMode={!!submittedID}
            draftID={draftID}
            updateDraftID={(id: string) => setDraftID(id)}
            access={access ?? { report: 'new_pr_timesheet' }}
            changeToBundle={() => {
              setStep(1);
              setUseBundleProcess(true);
              setShouldRemoveReimbursement(false);
            }}
            fromLastTimesheet={fromLastTimesheet}
            afterUpdate={() => {
              setFromLastTimesheet(false);
              setShouldRemoveReimbursement(false);
              setFilesToDeleteDefault({});
              localStorage.removeItem('temp-timesheet');
              localStorage.removeItem('temp-timesheet-id');
              localStorage.removeItem('temp-approval');
              localStorage.removeItem('temp-timesheet-updatedAt');
              localStorage.removeItem('temp-company');
            }}
            resetDraft={resetTimesheet}
            afterCheck={(show) => {
              setShowErrors(show);
            }}
            removeReimbursement={shouldRemoveReimbursement}
            afterApproval={afterApproval}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default TimeSheetTableContainer;
